import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

import AssetImage from "src/components/wizard/Item/AssetImage";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import useQueryParams from "src/hooks/useQueryParams";
import { CREATE_INVENTORY_TRANSACTION_MUTATION } from "src/constants/graphql";
import { ASSET_REPLAY_LOGS_QUERY } from "src/constants/graphql/queries";
import {
  getFormattedLocations,
  getOldRackLocations,
  getRackLocationsChanges,
} from "src/components/modals/InventoryTransactionMultipleModal/utils";

const parseJsonParam = (value, validator) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (validator && !validator(parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const parseWarehouseLocationsParam = value => {
  return parseJsonParam(value, parsed => 
    Array.isArray(parsed) && 
    parsed.length > 0 && 
    parsed.every(loc => loc.id && loc.location_name)
  );
};

const parseWarehouseParam = value => {
  return parseJsonParam(value, parsed => parsed?.id && parsed?.name);
};

const parseMobileWarehouseLocationParam = value => {
  return parseJsonParam(value, parsed => parsed?.id && parsed?.location_name);
};

export default function ConfirmMoveToSelectedLocationModal({ asset, sourceRackLocationIndex = 0 }) {
  const navigate = useNavigate();
  const params = useQueryParams();
  const { notifySuccess, notifyError } = useNotification();
  const [loading, setLoading] = useState(false);
  const rapidScanEnabled = params.get("rapidScan") === "true";
  const didAutoConfirmRef = useRef(false);

  const [createInventoryTransaction] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);

  // Parse new location data from URL params
  const newWarehouseLocations = parseWarehouseLocationsParam(params.get("newWarehouseLocations"));
  const newWarehouse = parseWarehouseParam(params.get("newWarehouse"));
  const newMobileWarehouseLocation = parseMobileWarehouseLocationParam(params.get("newMobileWarehouseLocation"));

  // Display text for the selected location
  const selectedLocationText = newWarehouseLocations
    ? newWarehouseLocations.map(loc => loc.location_name).join(", ")
    : "";

  const goBackToManualSearch = () => {
    params.delete("code");
    params.set("manualSearch", "true");
    navigate({ search: params.toString() });
  };

  const handleConfirm = () => {
    if (!asset?.id || !asset?.assetId) {
      return notifyError("Missing asset data");
    }

    if (!newWarehouseLocations || newWarehouseLocations.length === 0) {
      return notifyError("Missing selected location");
    }

    const originalLocations = asset?.rackLocations || [];
    if (!Array.isArray(originalLocations) || originalLocations.length === 0) {
      return notifyError("Missing original locations");
    }
    if (
      typeof sourceRackLocationIndex !== "number" ||
      sourceRackLocationIndex < 0 ||
      sourceRackLocationIndex >= originalLocations.length
    ) {
      return notifyError("Invalid selected location");
    }

    const nextLocations = originalLocations.map((location, index) => {
      if (index !== sourceRackLocationIndex) return location;

      // Build the updated location with exact warehouse_locations from the first asset
      const updatedLocation = {
        ...location,
        warehouse_locations: newWarehouseLocations,
      };

      // Update warehouse if provided
      if (newWarehouse) {
        updatedLocation.warehouse = newWarehouse;
      }

      // Update mobile_warehouse_location if provided
      if (newMobileWarehouseLocation) {
        updatedLocation.mobile_warehouse_location = newMobileWarehouseLocation;
      }

      return updatedLocation;
    });

    const values = { locations: nextLocations };
    const initialValues = { locations: originalLocations };

    const oldRackLocations = getOldRackLocations(initialValues.locations);
    const rackLocationsChanges = getRackLocationsChanges(values.locations, initialValues.locations);
    const rackLocations = getFormattedLocations(
      values,
      true
    );

    const variables = {
      code: asset.assetId,
      notes: "",
      senderName: "",
      carrierName: "",
      trackingNumber: "",
      orderNumber: "",
      poNumber: "",
      quantity: 0,
      rackLocations,
      rackLocationsChanges,
      oldRackLocations,
      id: asset.id,
      actionSource: "inventory_move",
    };

    const refetchQueries = [{ query: ASSET_REPLAY_LOGS_QUERY, variables: { assetId: asset.id } }];

    setLoading(true);
    createInventoryTransaction({
      variables,
      refetchQueries,
      onSuccess: () => {
        notifySuccess("Item moved successfully!");

        const currentMovedIds = params.get("movedAssetIds");
        const movedIdsArray = currentMovedIds ? currentMovedIds.split(",") : [];
        if (!movedIdsArray.includes(asset.id)) {
          movedIdsArray.push(asset.id);
        }
        params.set("movedAssetIds", movedIdsArray.join(","));

        setLoading(false);
        goBackToManualSearch();
      },
      onFailure: errors => {
        setLoading(false);
        notifyError(errors?.submit);
      },
    });
  };

  useEffect(() => {
    if (!rapidScanEnabled) return;
    if (didAutoConfirmRef.current) return;
    didAutoConfirmRef.current = true;
    handleConfirm();
  }, [rapidScanEnabled]);

  if (rapidScanEnabled) {
    return (
      <Dialog open onClose={goBackToManualSearch} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
          <Typography align="center" color="text.primary" fontSize={18}>
            Moving this item to{" "}
            <Box component="span" fontWeight={500}>
              {selectedLocationText}
            </Box>
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={1}>
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              alignItems="center"
              width="100%"
              mt={1}
            >
              <Box position="relative" display="inline-flex">
                <CircularProgress
                  size={100}
                  thickness={2}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-50px",
                    marginLeft: "-50px",
                    color: "primary.main",
                  }}
                />
                <Box borderRadius="50%" overflow="hidden">
                  <AssetImage width={90} height={80} image={asset?.images?.[0]?.url} />
                </Box>
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography align="center" fontWeight={500} noWrap>
                  {asset?.itemId}
                </Typography>
                <Typography
                  align="center"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {asset?.description || "No description"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={goBackToManualSearch} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
        <Typography fontSize={22} fontWeight={500} color="textPrimary" align="center">
          Confirm Move
        </Typography>
        <IconButton onClick={goBackToManualSearch} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={1}>
          <Typography align="center" color="text.primary" sx={{ fontSize: 16 }}>
            Are you sure you want to move this item to{" "}
            <Box component="span" fontWeight={500}>
              {selectedLocationText}
            </Box>
            ?
          </Typography>

          <Box display="flex" gap={2} alignItems="center" width="100%" mt={1}>
            <AssetImage width={90} height={80} image={asset?.images?.[0]?.url} />
            <Box flex={1} minWidth={0}>
              <Typography fontWeight={500} noWrap>
                {asset?.itemId}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {asset?.description || "No description"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="outlined" onClick={goBackToManualSearch} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading && <CircularProgress size={18} />}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
